import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import UploaderWizard from 'Components/UploaderWizard';
import { selectGlobalRulesBulkUploadConfig } from 'Selectors';
import { postGlobalRules } from 'Services';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useHistory } from 'react-router-dom';
import Layout from 'Components/Layout';

import styles from './BulkUploadPage.css';

const BulkUploadPage = () => {
    const { t } = useTranslation();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const globalRulesUploadConfig = useSelector(selectGlobalRulesBulkUploadConfig);

    if (!globalRulesUploadConfig) return null;
    const rulesTypeText = t('Global Rules');

    const { maxFileSize, fileTypeOptions, columns } = globalRulesUploadConfig;
    const processedUploaderConfig = {
        maxSize: maxFileSize,
        fileTypes: fileTypeOptions,
        columns: columns.map(({ required, key, title, acceptableHeaders }: UploaderColumn) => ({
            required,
            accessor: key,
            Header: title,
            acceptableHeaders
        }))
    };

    const postRules = async (parsedFileData: any, matchedColumnsMap: Record<string, any>) => {
        const Payload = parsedFileData.map((item: any) => ({
            id: item[matchedColumnsMap.ruleId],
            score: item[matchedColumnsMap.score],
            conclusion: item[matchedColumnsMap.conclusion],
            active: item[matchedColumnsMap.active],
            threshold: item[matchedColumnsMap.threshold]
        }));
        return postGlobalRules(Payload).then(async ({ data }) => {
            if (data) {
                history.push('/global-rules');
                const numUpdatedRules = data?.numOfUpdatedRules;
                const content = (
                    <>
                        <div className={styles.successContent}>
                            <span className={styles.bolded}>
                                {t('numUpdatedRules', { count: numUpdatedRules, rulesTypeText })}
                            </span>
                            {t('numUpdatedRulesPt2', { count: numUpdatedRules, rulesTypeText })}
                        </div>
                    </>
                );
                addModal({
                    id: 'global-bulk-upload-success',
                    status: 'success',
                    title: t('Global Rule Settings Uploaded'),
                    cancelText: t('Close'),
                    content
                });
            }
        });
    };

    const errorContentRenderer = (result: UploadErrorsType) => {
        return (
            <div className={styles.errorBodyMsg}>
                <span className={styles.errUnsuccessfulRulesMsg}>
                    {t('uploadBulkRulesErrors', { count: result?.errors?.length })}{' '}
                </span>
                {t('instructions in event of global rules upload errors')}
            </div>
        );
    };

    const previewContentRenderer = (parsedFileTableData: ParsedFileTableData) => {
        return (
            <>
                {t('Your file contains')} {t('settings for')}
                <span className={styles.rulesNumber}>
                    {t('numPreviewRules', { count: parsedFileTableData?.data?.length })}.
                </span>
            </>
        );
    };

    return (
        <Layout>
            <UploaderWizard
                uploaderConfig={processedUploaderConfig}
                rulesTypeText={rulesTypeText}
                rulesType='globalRules'
                postFn={postRules}
                onCancel={() => history.push('/global-rules')}
                errorContentRenderer={errorContentRenderer}
                previewContentRenderer={previewContentRenderer}
            />
        </Layout>
    );
};

export default BulkUploadPage;
